<div class="flex items-center justify-between mb-8">
    <div>
        <h1 class="text-3xl font-extrabold text-slate-800 tracking-tight">Mi Panel Estudiantil</h1>
        <p class="text-sm font-medium text-slate-500 mt-1">Revisa tu situación académica y asistencia actual.</p>
    </div>
    <div class="p-3 bg-emerald-50 rounded-2xl text-emerald-600 shadow-sm border border-emerald-100">
        <mat-icon svgIcon="heroicons_outline:academic-cap" class="icon-size-6"></mat-icon>
    </div>
</div>

<!-- Loading -->
<ng-container *ngIf="loading">
    <div class="flex flex-col items-center justify-center p-12 bg-white rounded-3xl border border-dashed border-slate-200">
        <mat-spinner diameter="40"></mat-spinner>
        <p class="mt-4 text-sm font-medium text-slate-500">Cargando tu información...</p>
    </div>
</ng-container>

<!-- Empty State -->
<ng-container *ngIf="!loading && !studentData">
    <div class="flex flex-col items-center justify-center p-12 bg-white rounded-3xl border border-dashed border-slate-200">
        <div class="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center mb-4">
            <mat-icon svgIcon="heroicons_outline:face-frown" class="text-slate-400 icon-size-8"></mat-icon>
        </div>
        <h3 class="text-lg font-bold text-slate-700">Sin datos registrados</h3>
        <p class="text-sm text-slate-500 text-center max-w-sm mt-1">
            No hemos podido cargar tu perfil de estudiante. Comunícate con tu institución educativa si este problema persiste.
        </p>
    </div>
</ng-container>

<!-- Student Data -->
<div *ngIf="!loading && studentData" class="grid grid-cols-1 md:grid-cols-12 gap-6">

    <!-- Hero Card -->
    <div class="md:col-span-12 xl:col-span-7 bg-white rounded-3xl p-6 sm:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6 shadow-sm border border-slate-100 relative overflow-hidden">
        <div class="absolute -right-20 -top-20 w-64 h-64 bg-emerald-50 rounded-full blur-3xl pointer-events-none"></div>

        <div class="w-24 h-24 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-4xl shadow-lg shadow-emerald-600/20 shrink-0 z-10">
            {{ studentData.persona.name.charAt(0) | uppercase }}
        </div>

        <div class="flex-1 text-center sm:text-left z-10">
            <span class="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-800 tracking-widest uppercase mb-3">
                Estudiante Activo
            </span>
            <h2 class="text-2xl font-extrabold text-slate-800 leading-tight mb-2">
                {{ studentData.persona.name }}
            </h2>

            <div class="flex flex-wrap items-center justify-center sm:justify-start gap-3 mt-4">
                <div class="flex flex-col bg-slate-50 px-4 py-2.5 rounded-xl border border-slate-100 min-w-[120px]">
                    <span class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Grado y Sección</span>
                    <span class="text-sm font-bold text-slate-700">{{ studentData.seccion.name }}</span>
                </div>
                <div class="flex flex-col bg-slate-50 px-4 py-2.5 rounded-xl border border-slate-100 min-w-[120px]">
                    <span class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Turno</span>
                    <span class="text-sm font-bold text-slate-700">{{ studentData.turno }}</span>
                </div>
            </div>
        </div>
    </div>

    <!-- School Details Card -->
    <div class="md:col-span-6 xl:col-span-5 bg-white rounded-3xl p-6 shadow-sm border border-slate-100 flex flex-col justify-center relative overflow-hidden">
        <div class="absolute -left-16 -bottom-16 w-32 h-32 bg-indigo-50 rounded-full blur-2xl pointer-events-none"></div>

        <div class="flex items-center gap-4 mb-4 z-10">
            <div class="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0">
                <mat-icon svgIcon="heroicons_outline:building-office-2"></mat-icon>
            </div>
            <div>
                <span class="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-0.5">Institución</span>
                <h3 class="text-[15px] font-bold text-slate-800 leading-snug">{{ studentData.institucion.name }}</h3>
            </div>
        </div>

        <div class="border-t border-slate-100 pt-4 z-10">
            <span class="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-2">Asistencia de Hoy</span>

            <div *ngIf="studentData.ultimaMarcacion" class="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl p-3">
                <div class="flex items-center gap-3">
                    <ng-container [ngSwitch]="studentData.ultimaMarcacion.resultado.id">
                        <div *ngSwitchCase="1" class="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                            <mat-icon svgIcon="heroicons_solid:check" class="icon-sm"></mat-icon>
                        </div>
                        <div *ngSwitchCase="3" class="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                            <mat-icon svgIcon="heroicons_solid:clock" class="icon-sm"></mat-icon>
                        </div>
                        <div *ngSwitchDefault class="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                            <mat-icon svgIcon="heroicons_solid:information-circle" class="icon-sm"></mat-icon>
                        </div>
                    </ng-container>

                    <div class="flex flex-col leading-tight">
                        <span class="text-xs font-bold" [ngClass]="{
                            'text-emerald-700': studentData.ultimaMarcacion.resultado.id === 1,
                            'text-amber-700': studentData.ultimaMarcacion.resultado.id === 3,
                            'text-indigo-700': studentData.ultimaMarcacion.resultado.id !== 1 && studentData.ultimaMarcacion.resultado.id !== 3
                        }">
                            {{ studentData.ultimaMarcacion.resultado.name }}
                        </span>
                        <span class="text-[10px] text-slate-500 font-medium">Marcado a las {{ studentData.ultimaMarcacion.hora | slice:0:5 }} hrs</span>
                    </div>
                </div>
                <span class="text-xs font-bold text-slate-400">{{ studentData.ultimaMarcacion.fecha | date:'dd/MM' }}</span>
            </div>

            <div *ngIf="!studentData.ultimaMarcacion" class="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-xl p-3">
                <div class="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500">
                    <div class="w-2 h-2 rounded-full bg-slate-400 animate-pulse"></div>
                </div>
                <span class="text-xs font-bold text-slate-500">Aún no hay marcación registrada hoy.</span>
            </div>
        </div>
    </div>

</div>
